import { Sidebar, SidebarToggle } from "@/components/sidebar";
import { MobileMessageDialog } from "@/dialogs/mobile-message-dialog";
import { usePageTransition } from "@/shared";
import ErrorBoundary from "@components/error-boundary";
import { UserNavMenu } from "@components/user-nav-menu";
import { PageIntroPopup } from "@features/page-intro";
import { PdfExportManager } from "@features/pdf-export";
import { cn } from "@lib/cn";
import { Suspense } from "react";
import { useLocation } from "react-router-dom";
import PlatformFunnelOverlay from "@/pages/how-to-start/platform-funnel-overlay";

export const BaseLayout = ({
  children,
  className,
  fallback,
  hideHeader,
  hideSidebar,
  noPadding,
  fillViewport,
  transitionKey,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  fallback?: React.ReactNode;
  hideHeader?: boolean;
  hideSidebar?: boolean;
  // Pins the page to exactly one viewport and lays the content out as a flex
  // column, so a child marked `flex-1` fills the space left under the header
  // instead of the page growing past the fold. Used by the dashboard, whose
  // documents list should reach the bottom of the screen and scroll in place.
  fillViewport?: boolean;
  // Drops the default tw-px-6 tw-py-3.5 inner padding. Used by routes that
  // render their own chrome (e.g. the chrome-extension side panel).
  noPadding?: boolean;
  transitionKey?: string;
  title?: string;
}) => {
  const location = useLocation();

  return (
    <ErrorBoundary feature={location.pathname as string}>
      <div
        id="base-layout"
        className={cn(
          "tw-mx-auto tw-flex tw-min-w-0 tw-max-w-full tw-bg-[#FBFBFC]",
          // noPadding routes (chrome-extension side panel) need an exact
          // viewport height so descendants using `h-full` can resolve
          // against a concrete container. The default routes keep
          // `min-h-screen` so long pages can still grow and scroll.
          noPadding || fillViewport ? "tw-h-screen" : "tw-min-h-screen",
          className,
        )}
      >
        {!hideSidebar && <Sidebar />}
        <Suspense fallback={fallback}>
          <PageTransitionWrapper
            key={transitionKey ?? location.pathname}
            className={className}
            noPadding={noPadding}
            fillViewport={fillViewport}
          >
            {!hideHeader && (
              <div className="tw-mb-6 tw-flex tw-w-full tw-items-center tw-justify-between -xl:tw-hidden">
                <div className="tw-flex tw-items-center">
                  <SidebarToggle />
                  <span className="tw-block tw-h-4 tw-w-px tw-bg-blue-100" />
                  <h2 className="tw-m-0 tw-ml-2 tw-font-sans tw-text-lg tw-font-semibold tw-text-blue">
                    {title}
                  </h2>
                </div>
                <UserNavMenu />
              </div>
            )}
            {children}
            <PlatformFunnelOverlay />
          </PageTransitionWrapper>
        </Suspense>
        <MobileMessageDialog />
        <PdfExportManager />
        <PageIntroPopup />
      </div>
    </ErrorBoundary>
  );
};

const PageTransitionWrapper = ({
  children,
  className,
  noPadding,
  fillViewport,
}: {
  children: React.ReactNode;
  className?: string | undefined;
  noPadding?: boolean;
  fillViewport?: boolean;
}) => {
  const { transitionClasses } = usePageTransition();
  return (
    <div
      className={cn(
        "tw-min-w-0 tw-grow",
        !noPadding && "tw-px-6 tw-py-3.5",
        // Without an explicit height, `tw-grow` only stretches against
        // siblings in the flex container — descendants relying on
        // `h-full` still resolve to `auto`. `noPadding` is the
        // chrome-extension case which needs that chain unbroken.
        noPadding && "tw-h-full tw-min-h-0",
        // min-h-0 lets the flex child actually shrink, without which an
        // overflowing table would push the card past the viewport instead of
        // scrolling inside it.
        //
        // overflow-y-auto keeps the overflow inside this column. The row above
        // is h-screen with the sidebar as a sibling, so anything taller than
        // the viewport scrolled the page and took the sidebar with it — which
        // is what a short window did once the dashboard grew a Learn row under
        // the list. pb-6 so the last card is not flush against the edge.
        fillViewport &&
          "tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-overflow-y-auto tw-pb-6",
        transitionClasses,
        className,
      )}
    >
      {children}
    </div>
  );
};
